import React, { useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight, faShieldAlt } from '@fortawesome/free-solid-svg-icons';
import { GET_ALL_DAMAGE_CLAIMS } from '../../services/damageClaimService/adminQueries';
import { DamageClaimStatus, DamageType } from '../../../__generated__/graphql';
import { URLS } from '../../../constants/urls';
import { getShortIdFromUuid, formatShortId } from '../../utils/short-id';
import {
  Alert,
  Badge,
  BadgeTone,
  Card,
  CardRow,
  EmptyState,
  LoadingBlock,
  PageContainer,
  PageHeader,
  Select,
  Stack,
  Tabs,
} from '../../components/ui';

const ClaimMain = styled.div`
  ${tw`flex items-start gap-4 min-w-0 flex-1`}
`;

const Thumb = styled.div`
  ${tw`w-16 h-12 rounded-md overflow-hidden shrink-0 flex items-center justify-center`}
  background-color: var(--color-bg-secondary);
  color: var(--color-text-tertiary);
`;

const ThumbImg = styled.img`
  ${tw`w-full h-full object-cover`}
`;

const ClaimText = styled.div`
  ${tw`min-w-0 flex-1`}
`;

const ClaimTitle = styled.div`
  ${tw`flex flex-wrap items-center gap-2 text-sm font-semibold`}
  color: var(--color-text-primary);
`;

const ClaimMeta = styled.div`
  ${tw`flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs`}
  color: var(--color-text-secondary);
`;

const Amounts = styled.div`
  ${tw`flex items-center gap-6 shrink-0 text-sm`}
`;

const Amount = styled.div`
  ${tw`text-right`}
`;

const AmountLabel = styled.div`
  ${tw`text-[11px] uppercase tracking-wider`}
  color: var(--color-text-tertiary);
`;

const AmountValue = styled.div<{ $positive?: boolean }>`
  ${tw`font-semibold tabular-nums`}
  color: ${({ $positive }) => ($positive ? '#047857' : 'var(--color-text-primary)')};
`;

const Chevron = styled.div`
  ${tw`shrink-0`}
  color: var(--color-text-tertiary);
`;

const FilterRow = styled.div`
  ${tw`flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between`}
`;

const StatusSelect = styled(Select)`
  ${tw`w-full sm:w-56`}
`;

const getStatusLabel = (status: DamageClaimStatus): string => {
  switch (status) {
    case DamageClaimStatus.Pending:
      return 'Pending';
    case DamageClaimStatus.UnderReview:
      return 'Under Review';
    case DamageClaimStatus.Approved:
      return 'Approved';
    case DamageClaimStatus.PartiallyApproved:
      return 'Partially Approved';
    case DamageClaimStatus.Rejected:
      return 'Rejected';
    case DamageClaimStatus.Resolved:
      return 'Resolved';
    case DamageClaimStatus.Withdrawn:
      return 'Withdrawn';
    default:
      return status;
  }
};

const getStatusTone = (status: DamageClaimStatus): BadgeTone => {
  switch (status) {
    case DamageClaimStatus.Pending:
      return 'warning';
    case DamageClaimStatus.UnderReview:
      return 'info';
    case DamageClaimStatus.Approved:
    case DamageClaimStatus.PartiallyApproved:
      return 'success';
    case DamageClaimStatus.Rejected:
      return 'error';
    default:
      return 'neutral';
  }
};

const getDamageTypeLabel = (type: DamageType): string => {
  switch (type) {
    case DamageType.Physical:
      return 'Physical Damage';
    case DamageType.Mechanical:
      return 'Mechanical Issue';
    default:
      return type;
  }
};

const formatDate = (dateValue: string | number | Date): string => {
  let date: Date;
  if (typeof dateValue === 'string') {
    const parsed = parseInt(dateValue);
    if (!isNaN(parsed) && String(parsed) === dateValue) {
      date = new Date(parsed);
    } else {
      date = new Date(dateValue);
    }
  } else {
    date = new Date(dateValue);
  }

  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const formatTripDates = (startAt: string | number, endAt: string | number): string => {
  const start = new Date(typeof startAt === 'string' ? parseInt(startAt) : startAt);
  const end = new Date(typeof endAt === 'string' ? parseInt(endAt) : endAt);
  const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  return `${start.toLocaleDateString('en-US', options)} - ${end.toLocaleDateString('en-US', options)}`;
};

type QuickFilter = '' | DamageClaimStatus.Pending | DamageClaimStatus.UnderReview;

const AdminDamageClaimsPage: React.FC = () => {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<DamageClaimStatus | ''>('');

  const { data, loading, error } = useQuery(GET_ALL_DAMAGE_CLAIMS, {
    variables: statusFilter ? { status: statusFilter } : {},
    fetchPolicy: 'network-only',
  });

  const claims = data?.allDamageClaims || [];

  // Calculate stats
  const pendingCount = claims.filter((c: any) => c.status === DamageClaimStatus.Pending).length;
  const reviewCount = claims.filter((c: any) => c.status === DamageClaimStatus.UnderReview).length;
  const totalCount = claims.length;

  const handleClaimClick = (claimId: string) => {
    navigate(`/admin/damage-claims/${claimId}`);
  };

  const quickFilter: QuickFilter =
    statusFilter === '' ||
    statusFilter === DamageClaimStatus.Pending ||
    statusFilter === DamageClaimStatus.UnderReview
      ? (statusFilter as QuickFilter)
      : '';

  return (
    <PageContainer width="lg" canvas>
      <PageHeader
        eyebrow="Admin"
        title="Damage claims"
        description="Every claim across the platform. Open one to review photos, the counterparty response and the decision."
      />

      <Stack>
        <FilterRow>
          <Tabs<QuickFilter>
            aria-label="Quick filter"
            items={[
              { key: '', label: 'All', count: totalCount },
              { key: DamageClaimStatus.Pending, label: 'Pending', count: pendingCount },
              { key: DamageClaimStatus.UnderReview, label: 'Under review', count: reviewCount },
            ]}
            value={quickFilter}
            onChange={key => setStatusFilter(key)}
          />
          <StatusSelect
            aria-label="Status"
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value as DamageClaimStatus | '')}
          >
            <option value="">All statuses</option>
            <option value={DamageClaimStatus.Pending}>Pending</option>
            <option value={DamageClaimStatus.UnderReview}>Under Review</option>
            <option value={DamageClaimStatus.Approved}>Approved</option>
            <option value={DamageClaimStatus.PartiallyApproved}>Partially Approved</option>
            <option value={DamageClaimStatus.Rejected}>Rejected</option>
            <option value={DamageClaimStatus.Resolved}>Resolved</option>
            <option value={DamageClaimStatus.Withdrawn}>Withdrawn</option>
          </StatusSelect>
        </FilterRow>

        {loading && <LoadingBlock label="Loading claims" />}

        {error && (
          <Alert tone="error" title="Error loading claims">
            {error.message}
          </Alert>
        )}

        {!loading && !error && (
          <Card>
            {claims.length === 0 ? (
              <EmptyState
                icon={faShieldAlt}
                title="No damage claims"
                description="No claims match the selected filter."
              />
            ) : (
              claims.map((claim: any) => {
                const car = claim.booking?.car;
                return (
                  <CardRow key={claim.id} $interactive onClick={() => handleClaimClick(claim.id)}>
                    <ClaimMain>
                      <Thumb>
                        {car?.thumbnailUrl ? (
                          <ThumbImg
                            src={`${URLS.S3_BUCKET.CAR_THUMBNAILS}/${car.thumbnailUrl}`}
                            alt={`${car.make} ${car.model}`}
                          />
                        ) : (
                          <FontAwesomeIcon icon={faShieldAlt} />
                        )}
                      </Thumb>
                      <ClaimText>
                        <ClaimTitle>
                          Claim #{claim.claimNum}
                          <Badge tone={getStatusTone(claim.status)} dot size="sm">
                            {getStatusLabel(claim.status)}
                          </Badge>
                          <Badge tone="neutral" size="sm">
                            {formatShortId(claim.shortId || getShortIdFromUuid(claim.id))}
                          </Badge>
                        </ClaimTitle>
                        <ClaimMeta>
                          <span>{getDamageTypeLabel(claim.damageType)}</span>
                          {car && (
                            <span>
                              {car.year} {car.make} {car.model}
                              {claim.booking?.startAt && claim.booking?.endAt
                                ? ` · ${formatTripDates(claim.booking.startAt, claim.booking.endAt)}`
                                : ''}
                            </span>
                          )}
                          <span>Submitted {formatDate(claim.createdAt)}</span>
                        </ClaimMeta>
                        <ClaimMeta>
                          <span>
                            Host:{' '}
                            {claim.booking?.host?.displayName ||
                              claim.booking?.host?.firstName ||
                              'N/A'}
                          </span>
                          <span>
                            Guest:{' '}
                            {claim.booking?.guest?.displayName ||
                              claim.booking?.guest?.firstName ||
                              'N/A'}
                          </span>
                          <span>
                            Reporter:{' '}
                            {claim.reporter?.displayName || claim.reporter?.firstName || 'N/A'}
                          </span>
                          {claim.repairEstimateRange && (
                            <span>
                              Estimate {claim.repairEstimateRange.replace(/_/g, ' ').toLowerCase()}
                            </span>
                          )}
                        </ClaimMeta>
                      </ClaimText>
                    </ClaimMain>
                    <Amounts>
                      {claim.claimAmount ? (
                        <Amount>
                          <AmountLabel>Claimed</AmountLabel>
                          <AmountValue>${claim.claimAmount.toFixed(2)}</AmountValue>
                        </Amount>
                      ) : null}
                      {claim.approvedAmount ? (
                        <Amount>
                          <AmountLabel>Approved</AmountLabel>
                          <AmountValue $positive>${claim.approvedAmount.toFixed(2)}</AmountValue>
                        </Amount>
                      ) : null}
                      <Chevron>
                        <FontAwesomeIcon icon={faChevronRight} />
                      </Chevron>
                    </Amounts>
                  </CardRow>
                );
              })
            )}
          </Card>
        )}
      </Stack>
    </PageContainer>
  );
};

export default AdminDamageClaimsPage;
